import React, { useEffect, useState } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useTranslation } from 'react-i18next';
import { 
  LuLayoutDashboard, LuServer, LuShieldCheck, 
  LuUsers, LuLifeBuoy, LuLogOut,
  LuLanguages, LuBanknote, LuSun, LuMoon,
  LuMenu, LuX, LuKey, LuChevronDown, LuUser, LuPackage
} from 'react-icons/lu';
import { useTheme } from '../ThemeContext';
import { useBranding } from '../BrandingContext';
import './ClientLayout.css';

// --- Компонент: Переключатель темы ---
const ThemeSwitcherImpl = () => {
  const { theme, toggleTheme } = useTheme();
  return (
    <button className="btn-icon" onClick={toggleTheme} title={theme === 'light' ? 'Включить темную' : 'Включить светлую'}>
      {theme === 'light' ? <LuMoon /> : <LuSun />}
    </button>
  );
};

// --- Компонент Переключателей ---
const LanguageCurrencySwitcherImpl = ({ activeLanguages, activeCurrencies }) => {
  const { t, i18n } = useTranslation();
  const { token, user, setUser, currency, setCurrency } = useAuth(); 
  const currentLang = i18n.resolvedLanguage;
  const currentCurrency = currency;
  
  // Фильтруем языки и валюты по активным
  const availableLanguages = [
    { code: 'ru', label: t('languages.ru') },
    { code: 'ua', label: t('languages.ua') },
    { code: 'cn', label: t('languages.cn') },
    { code: 'en', label: t('languages.en') }
  ].filter(lang => activeLanguages.includes(lang.code));
  
  const availableCurrencies = [
    { code: 'uah', label: '₴ UAH' },
    { code: 'rub', label: '₽ RUB' },
    { code: 'usd', label: '$ USD' }
  ].filter(curr => activeCurrencies.includes(curr.code));

  const saveSettings = async (settingsToSave) => {
    if (!token) return;
    try {
      const res = await fetch('/api/client/settings', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settingsToSave)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        console.error('Не удалось сохранить настройки:', data.message || res.status);
        return false;
      }
      return true;
    } catch (e) {
      console.error('Не удалось сохранить настройки', e);
      return false;
    }
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem('userLang', lng);
    if (user) setUser(prevUser => ({ ...prevUser, preferred_lang: lng }));
    saveSettings({ lang: lng, preferred_lang: lng });
  };

  const changeCurrency = (curr) => {
    setCurrency(curr);
    localStorage.setItem('userCurrency', curr);
    if (user) setUser(prevUser => ({ ...prevUser, preferred_currency: curr }));
    saveSettings({ preferred_currency: curr, currency: curr });
  };

  return (
    <div className="settings-switcher">
      <div className="setting-control">
        <label htmlFor="currency-select"><LuBanknote /></label>
        <select id="currency-select" value={currentCurrency} onChange={(e) => changeCurrency(e.target.value)} className="lang-select">
          {availableCurrencies.map(curr => (
            <option key={curr.code} value={curr.code}>{curr.label}</option>
          ))}
        </select>
      </div>
      <div className="setting-control">
        <label htmlFor="lang-select"><LuLanguages /></label>
        <select id="lang-select" value={currentLang} onChange={(e) => changeLanguage(e.target.value)} className="lang-select">
          {availableLanguages.map(lang => (
            <option key={lang.code} value={lang.code}>{lang.label}</option>
          ))}
        </select>
      </div>
    </div>
  );
};


// --- Основной компонент ClientLayout ---
export default function ClientLayout() {
  const { t, i18n } = useTranslation();
  const { user, setUser, logout, token, currency, setCurrency, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { branding, brandingLoading } = useBranding();
  const [showLanguageCurrencySwitcher, setShowLanguageCurrencySwitcher] = useState(true);
  const [activeLanguages, setActiveLanguages] = useState(['ru', 'ua', 'en', 'cn']);
  const [activeCurrencies, setActiveCurrencies] = useState(['uah', 'rub', 'usd']);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Восстановление языка и валюты из localStorage при первой загрузке (до ответа API)
  useEffect(() => {
    const storedLang = localStorage.getItem('userLang');
    const storedCurrency = localStorage.getItem('userCurrency');
    if (storedLang && i18n.resolvedLanguage !== storedLang) {
      i18n.changeLanguage(storedLang);
    }
    if (storedCurrency && currency !== storedCurrency) {
      setCurrency(storedCurrency);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Загрузка системных настроек (брендинг загружается в BrandingContext)
  useEffect(() => {
    const fetchSystemSettings = async () => {
      try {
        const response = await fetch('/api/public/system-settings');
        if (response.ok) {
          const data = await response.json();
          setShowLanguageCurrencySwitcher(data.show_language_currency_switcher !== false);
          if (data.active_languages && Array.isArray(data.active_languages)) {
            setActiveLanguages(data.active_languages);
          }
          if (data.active_currencies && Array.isArray(data.active_currencies)) {
            setActiveCurrencies(data.active_currencies);
          }
        }
      } catch (e) {
        console.error('Ошибка загрузки системных настроек:', e);
      }
    };
    fetchSystemSettings();
  }, []);

  useEffect(() => {
    // Если токена нет, не загружаем
    if (!token) return;
    
    // Если user уже есть, не загружаем снова
    if (user) {
      // Синхронизируем язык и валюту, если нужно
      const lang = user.preferred_lang || localStorage.getItem('userLang') || 'ru';
      const curr = user.preferred_currency || localStorage.getItem('userCurrency') || 'uah';
      if (i18n.resolvedLanguage !== lang) {
        i18n.changeLanguage(lang);
        localStorage.setItem('userLang', lang);
      }
      if (currency !== curr) {
        setCurrency(curr);
        localStorage.setItem('userCurrency', curr);
      }
      return;
    } 
    
    // Флаг для отмены запроса при размонтировании
    let cancelled = false;
    
    const fetchMeData = async () => {
      try {
        // Добавляем небольшую задержку для Telegram логина, чтобы токен успел установиться
        await new Promise(resolve => setTimeout(resolve, 50));
        
        if (cancelled) return;
        
        const response = await fetch('/api/client/me?force_refresh=true', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (cancelled) return; // Проверяем отмену
        
        if (!response.ok) {
          if (response.status === 401) {
            // Если получили 401, возможно токен еще не активирован (например, после Telegram логина)
            // Пробуем еще раз через небольшую задержку
            await new Promise(resolve => setTimeout(resolve, 200));
            if (cancelled) return;
            
            const retryResponse = await fetch('/api/client/me?force_refresh=true', {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (!retryResponse.ok) {
              if (retryResponse.status === 401) {
                logout(); 
                navigate('/login');
                return;
              }
              throw new Error('Ошибка загрузки');
            }
            
            const retryData = await retryResponse.json();
            const retryUserData = retryData.response || retryData;
            
            if (cancelled) return;
            
            setUser(retryUserData);
            
            const lang = retryUserData.preferred_lang || localStorage.getItem('userLang') || 'ru';
            const curr = retryUserData.preferred_currency || localStorage.getItem('userCurrency') || 'uah';
            if (i18n.resolvedLanguage !== lang) {
              i18n.changeLanguage(lang);
              localStorage.setItem('userLang', lang);
            }
            if (currency !== curr) {
              setCurrency(curr);
              localStorage.setItem('userCurrency', curr);
            }
            return;
          }
          throw new Error('Ошибка загрузки');
        }
        const data = await response.json();
        const userData = data.response || data;
        
        if (cancelled) return; // Проверяем отмену
        
        setUser(userData); 
        
        const lang = userData.preferred_lang || localStorage.getItem('userLang') || 'ru';
        const curr = userData.preferred_currency || localStorage.getItem('userCurrency') || 'uah';
        if (i18n.resolvedLanguage !== lang) {
          i18n.changeLanguage(lang);
          localStorage.setItem('userLang', lang);
        }
        if (currency !== curr) {
          setCurrency(curr);
          localStorage.setItem('userCurrency', curr);
        }
      } catch (err) { 
        if (!cancelled) {
          console.error('Ошибка загрузки пользователя в ClientLayout:', err);
        }
      } 
    };
    
    fetchMeData();
    
    // Очистка при размонтировании
    return () => {
      cancelled = true;
    };
  }, [token, user, setUser, logout, i18n, currency, setCurrency, navigate]);

  const handleLogout = () => {
    logout();
    navigate('/login'); 
  };
  
  // Отслеживание скролла для эффекта навигации
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Закрываем меню при смене маршрута
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setShowUserMenu(false);
  }, [location.pathname]);

  // Закрываем dropdown при клике вне
  useEffect(() => {
    const onDocClick = (e) => {
      const target = e.target;
      if (!(target instanceof Element)) return;
      if (target.closest('.nav-user-menu')) return;
      setShowUserMenu(false);
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

  // --- Функция изменения пароля ---
  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (!passwordForm.current_password || !passwordForm.new_password) {
      alert('Заполните все поля');
      return;
    }

    if (passwordForm.new_password.length < 6) {
      alert('Пароль должен содержать минимум 6 символов');
      return;
    }

    if (passwordForm.new_password !== passwordForm.confirm_password) {
      alert('Пароли не совпадают');
      return;
    }

    setPasswordLoading(true);
    try {
      const response = await fetch('/api/client/change-password', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          current_password: passwordForm.current_password,
          new_password: passwordForm.new_password
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Ошибка изменения пароля');
      }

      alert('Пароль успешно изменен!');
      setShowPasswordModal(false);
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (e) {
      alert(`Ошибка: ${e.message}`);
    } finally {
      setPasswordLoading(false);
    }
  };

  // Показываем загрузку если:
  // 1. Есть токен (пользователь должен быть залогинен)
  // 2. Данные еще загружаются в AuthContext ИЛИ еще не загружены
  if (token && (!user || authLoading)) {
    return <div className="loading-full">Загрузка кабинета...</div>
  }
  
  // Если нет токена, не показываем ничего (это не авторизованный пользователь)
  if (!token) {
    return null;
  }

  // Показываем loading до загрузки брендинга
  if (brandingLoading || !branding) {
    return (
      <div className="client-shell">
        <div className="loading-mini">Загрузка...</div>
      </div>
    );
  }

  return (
    <div className="client-shell client-layout-v3">
      {/* Верхнее навигационное меню (как в V3) */}
      <nav className={`client-top-nav ${scrolled ? 'scrolled' : ''}`}>
        <div className="nav-container">
          <div className="nav-brand">
            {branding.logo_url ? (
              <img
                src={branding.logo_url}
                alt={branding.site_name || 'Logo'}
                className="nav-logo-image"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            ) : null}
            <div className="logo-text">{branding.site_name || ''}</div>
            <span className="logo-subtitle">{branding.dashboard_tagline || ''}</span>
          </div>

          <div className="nav-menu-desktop">
            <NavLink
              to="/dashboard/subscription"
              className={`nav-item ${location.pathname === '/dashboard/subscription' ? 'active' : ''}`}
            >
              <LuLayoutDashboard className="nav-icon" />
              <span className="nav-label">{t('sidebar.status', 'Статус')}</span>
            </NavLink>
            <NavLink
              to="/dashboard/servers"
              className={`nav-item ${location.pathname === '/dashboard/servers' ? 'active' : ''}`}
            >
              <LuServer className="nav-icon" />
              <span className="nav-label">{t('sidebar.servers', 'Серверы')}</span>
            </NavLink>
            <NavLink
              to="/dashboard/tariffs"
              className={`nav-item ${location.pathname === '/dashboard/tariffs' ? 'active' : ''}`}
            >
              <LuShieldCheck className="nav-icon" />
              <span className="nav-label">{t('sidebar.tariffs', 'Тарифы')}</span>
            </NavLink>
            <NavLink
              to="/dashboard/options"
              className={`nav-item ${location.pathname === '/dashboard/options' ? 'active' : ''}`}
            >
              <LuPackage className="nav-icon" />
              <span className="nav-label">{t('sidebar.options', 'Опции')}</span>
            </NavLink>
            <NavLink
              to="/dashboard/referrals"
              className={`nav-item ${location.pathname === '/dashboard/referrals' ? 'active' : ''}`}
            >
              <LuUsers className="nav-icon" />
              <span className="nav-label">{t('sidebar.referrals', 'Рефералы')}</span>
            </NavLink>
            <NavLink
              to="/dashboard/support"
              className={`nav-item ${location.pathname.startsWith('/dashboard/support') ? 'active' : ''}`}
            >
              <LuLifeBuoy className="nav-icon" />
              <span className="nav-label">{t('sidebar.support', 'Поддержка')}</span>
            </NavLink>
            <NavLink
              to="/dashboard/profile"
              className={`nav-item ${location.pathname === '/dashboard/profile' ? 'active' : ''}`}
            >
              <LuUser className="nav-icon" />
              <span className="nav-label">{t('sidebar.profile', 'Профиль')}</span>
            </NavLink>
          </div>

          <div className="nav-actions">
            {showLanguageCurrencySwitcher && (
              <div className="desktop-settings-switcher">
                <LanguageCurrencySwitcherImpl activeLanguages={activeLanguages} activeCurrencies={activeCurrencies} />
              </div>
            )}
            <ThemeSwitcherImpl />

            <div className="nav-user-menu">
              <button
                className="nav-user-btn"
                onClick={() => setShowUserMenu((v) => !v)}
                type="button"
              >
                <LuUser className="user-icon" />
                <span className="user-email">{user?.email || 'User'}</span>
                <LuChevronDown className={`dropdown-arrow ${showUserMenu ? 'open' : ''}`} />
              </button>
              {showUserMenu && (
                <div className="user-dropdown-menu">
                  <div className="user-dropdown-header">
                    <div className="user-info">
                      <div className="user-name">{user?.email || 'User'}</div>
                      <div className="user-role">Клиент</div>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setShowPasswordModal(true);
                      setShowUserMenu(false);
                    }}
                    className="user-dropdown-item"
                    type="button"
                  >
                    <LuKey />
                    <span>Изменить пароль</span>
                  </button>
                  <button onClick={handleLogout} className="user-dropdown-item logout" type="button">
                    <LuLogOut />
                    <span>Выйти</span>
                  </button>
                </div>
              )}
            </div>

            <button
              className="mobile-menu-toggle"
              onClick={() => setIsMobileMenuOpen((v) => !v)}
              aria-label="Меню"
              type="button"
            >
              {isMobileMenuOpen ? <LuX /> : <LuMenu />}
            </button>
          </div>
        </div>

        {isMobileMenuOpen && (
          <>
            <div className="mobile-menu-overlay" onClick={() => setIsMobileMenuOpen(false)} />
            <div className="nav-menu-mobile">
              <NavLink
                to="/dashboard/subscription"
                className={`nav-item-mobile ${location.pathname === '/dashboard/subscription' ? 'active' : ''}`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <LuLayoutDashboard />
                <span>{t('sidebar.status', 'Статус')}</span>
              </NavLink>
              <NavLink
                to="/dashboard/servers"
                className={`nav-item-mobile ${location.pathname === '/dashboard/servers' ? 'active' : ''}`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <LuServer />
                <span>{t('sidebar.servers', 'Серверы')}</span>
              </NavLink>
              <NavLink
                to="/dashboard/tariffs"
                className={`nav-item-mobile ${location.pathname === '/dashboard/tariffs' ? 'active' : ''}`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <LuShieldCheck />
                <span>{t('sidebar.tariffs', 'Тарифы')}</span>
              </NavLink>
              <NavLink
                to="/dashboard/options"
                className={`nav-item-mobile ${location.pathname === '/dashboard/options' ? 'active' : ''}`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <LuPackage />
                <span>{t('sidebar.options', 'Опции')}</span>
              </NavLink>
              <NavLink
                to="/dashboard/referrals"
                className={`nav-item-mobile ${location.pathname === '/dashboard/referrals' ? 'active' : ''}`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <LuUsers />
                <span>{t('sidebar.referrals', 'Рефералы')}</span>
              </NavLink>
              <NavLink
                to="/dashboard/support"
                className={`nav-item-mobile ${location.pathname.startsWith('/dashboard/support') ? 'active' : ''}`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <LuLifeBuoy />
                <span>{t('sidebar.support', 'Поддержка')}</span>
              </NavLink>
              <NavLink
                to="/dashboard/profile"
                className={`nav-item-mobile ${location.pathname === '/dashboard/profile' ? 'active' : ''}`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <LuUser />
                <span>{t('sidebar.profile', 'Профиль')}</span>
              </NavLink>

              {showLanguageCurrencySwitcher && (
                <div className="mobile-settings-block">
                  <LanguageCurrencySwitcherImpl activeLanguages={activeLanguages} activeCurrencies={activeCurrencies} />
                </div>
              )}

              <button onClick={handleLogout} className="nav-item-mobile logout" type="button">
                <LuLogOut />
                <span>{t('header.logout', 'Выйти')}</span>
              </button>
            </div>
          </>
        )}
      </nav>

      <main className="client-main-content">
        <div className="client-page-wrapper">
          <Outlet />
        </div>
      </main>

      {/* Модальное окно изменения пароля */}
      {showPasswordModal && (
        <div 
          className="modal-overlay" 
          onClick={() => {
            if (!passwordLoading) {
              setShowPasswordModal(false);
              setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
            }
          }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
        >
          <div 
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'var(--bg-primary)',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '500px',
              width: '90%',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)'
            }}
          >
            <h2 style={{ marginTop: 0, marginBottom: '20px' }}>Изменение пароля</h2>
            
            <form onSubmit={handleChangePassword}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                  Текущий пароль:
                </label>
                <input
                  type="password"
                  value={passwordForm.current_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                  placeholder="Введите текущий пароль"
                  required
                  disabled={passwordLoading}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '6px',
                    border: '1px solid var(--border-color)',
                    fontSize: '14px',
                    backgroundColor: 'var(--bg-secondary)',
                    color: 'var(--text-primary)'
                  }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                  Новый пароль:
                </label>
                <input
                  type="password"
                  value={passwordForm.new_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                  placeholder="Введите новый пароль (минимум 6 символов)"
                  required
                  minLength={6}
                  disabled={passwordLoading}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '6px',
                    border: '1px solid var(--border-color)',
                    fontSize: '14px',
                    backgroundColor: 'var(--bg-secondary)',
                    color: 'var(--text-primary)'
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                  Подтвердите новый пароль:
                </label>
                <input
                  type="password"
                  value={passwordForm.confirm_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                  placeholder="Повторите новый пароль"
                  required
                  minLength={6}
                  disabled={passwordLoading}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '6px',
                    border: '1px solid var(--border-color)',
                    fontSize: '14px',
                    backgroundColor: 'var(--bg-secondary)',
                    color: 'var(--text-primary)'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordModal(false);
                    setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
                  }}
                  disabled={passwordLoading}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '6px',
                    border: '1px solid var(--border-color)',
                    backgroundColor: 'var(--bg-secondary)',
                    color: 'var(--text-primary)',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  disabled={passwordLoading}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '6px',
                    border: 'none',
                    backgroundColor: 'var(--accent-color)',
                    color: 'white',
                    cursor: passwordLoading ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    opacity: passwordLoading ? 0.6 : 1
                  }}
                >
                  {passwordLoading ? 'Обработка...' : 'Изменить пароль'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}